import React, { useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import ReactPlayer from "react-player"
import AccentTitleDivider from "../shared/accent-title-divider"
import FadedTitle from "../shared/faded-title"
import Grid from "../shared/grid"
import VisibilitySensor from "react-visibility-sensor"
import {
  CMSFunctionsWrapper,
  InfoWrapper,
  SectionBody,
  List,
  VideoContainer,
  StyledLink,
  HiddenLink,
  DetailsWrapper,
  Orange,
  Spacer,
  AnimatedImage,
  video1Style,
  video2Style,
  image3Style,
  content1Style,
  content2Style,
  content3Style,
} from "./cms-functions.style"
import VisibilityProvider from "../shared/visibility-provider"
import { T } from "../shared/typography.styles"
import ExternalLinks from "../../external-links"

const CMSFunctions = () => {
  const {
    createTitleSrc,
    maintainTitleSrc,
    digitalMapsImage,
    reviewTitleSrc,
  } = useStaticQuery(graphql`
    query {
      createTitleSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS/Desktop/faded_titles/create_fade_title.svg"
        }
      ) {
        publicURL
      }
      maintainTitleSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS/Desktop/faded_titles/maintain_fade_title.svg"
        }
      ) {
        publicURL
      }
      reviewTitleSrc: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS/Desktop/faded_titles/review_fade_title.svg"
        }
      ) {
        publicURL
      }
      digitalMapsImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Indoor Mapping/reviewing_digital_maps.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 700, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const [isVideo1Visible, setIsVideo1Visible] = useState(false)
  const [isVideo2Visible, setIsVideo2Visible] = useState(false)

  return (
    <>
      <AccentTitleDivider>Functions of the CMS</AccentTitleDivider>
      <CMSFunctionsWrapper>
        <Grid>
          <VideoContainer css={video1Style}>
            <VisibilitySensor
              minTopValue={50}
              partialVisibility={true}
              onChange={setIsVideo1Visible}
            >
              <ReactPlayer
                url="https://vimeo.com/427469488/48f3a34eba"
                playing={isVideo1Visible}
                loop={true}
                volume={0}
                muted={true}
                controls={false}
                playsinline={true}
              />
            </VisibilitySensor>
          </VideoContainer>
          <VisibilityProvider>
            <InfoWrapper css={content1Style}>
              <FadedTitle
                fadeSettings={{ lg: "left" }}
                sm={T.sm.h3.regular}
                md={T.md.h3.regular}
                lg={T.lg.h3.regular}
                image={createTitleSrc.publicURL}
                title={
                  <>
                    <Orange>Creating</Orange> Digital Maps
                  </>
                }
                alt={"Create"}
              />
              <SectionBody>
                Mappedin can create maps at scale for your venue and its mapping
                solution by leveraging automation and our professional mapping
                team. Easily create and edit your maps within our map design
                software.{" "}
                <HiddenLink
                  to={
                    ExternalLinks.hubspot.indoorMapping.choosingMappingProvider
                  }
                >
                  Mappedin is the best indoor mapping software
                </HiddenLink>{" "}
                to create accurate and unforgettable mapping experiences for
                your venue.
              </SectionBody>
              <List>
                <li>
                  <span>Convert 2D to 3D Maps</span>
                </li>
                <li>
                  <span>Apply Location and Styling Data</span>
                </li>
                <li>
                  <span>Include Branding in your Map Design</span>
                </li>
                <li>
                  <span>Geo-Reference to the Real World</span>
                </li>
              </List>
            </InfoWrapper>
          </VisibilityProvider>
          <Spacer />
          <VideoContainer css={video2Style}>
            <VisibilitySensor
              minTopValue={50}
              partialVisibility={true}
              onChange={setIsVideo2Visible}
            >
              <ReactPlayer
                url="https://vimeo.com/427469692/95d781fd7f"
                playing={isVideo2Visible}
                loop={true}
                volume={0}
                muted={true}
                controls={false}
                playsinline={true}
              />
            </VisibilitySensor>
          </VideoContainer>
          <VisibilityProvider>
            <InfoWrapper css={content2Style}>
              <FadedTitle
                sm={T.sm.h3.regular}
                md={T.md.h3.regular}
                lg={T.lg.h3.regular}
                fadeSettings={{ lg: "left" }}
                image={maintainTitleSrc.publicURL}
                title={
                  <>
                    <Orange>Maintaining</Orange> Digital Maps
                  </>
                }
                alt={"Maintain"}
              />
              <SectionBody>
                Keep your maps up to date with access to our collaborative
                Editor tools, ensuring your mapping experience is always
                accurate. Our reliable indoor mapping system makes mapping
                interiors and building spaces easier and helps venue owners and
                operators maintain accuracy across your indoor mapping
                platforms.
              </SectionBody>
              <DetailsWrapper>
                <List>
                  <li>
                    <span>Manage Indoor Location Details</span>
                  </li>
                  <li>
                    <span>Update Map Geometry</span>
                  </li>
                  <li>
                    <span>Add Events & Promotions</span>
                  </li>
                  <li>
                    <span>Draft & Publish Changes</span>
                  </li>
                </List>
              </DetailsWrapper>
              <StyledLink href="/mapping/map-editor/">
                Explore Our Map Editor
              </StyledLink>
            </InfoWrapper>
          </VisibilityProvider>
          <Spacer />
          <VisibilityProvider>
            <AnimatedImage
              fluid={digitalMapsImage.childImageSharp.fluid}
              css={image3Style}
              imgStyle={{ objectFit: "contain" }}
            />
          </VisibilityProvider>
          <VisibilityProvider>
            <InfoWrapper css={content3Style}>
              <FadedTitle
                sm={T.sm.h3.regular}
                md={T.md.h3.regular}
                lg={T.lg.h3.regular}
                fadeSettings={{ lg: "left" }}
                image={reviewTitleSrc.publicURL}
                title={
                  <>
                    <Orange>Reviewing</Orange> Digital Maps
                  </>
                }
                alt={"Maintain"}
              />
              <SectionBody>
                Understand visitors to your buildings, what they are searching
                for, and where they are wayfinding to. Collect and visualize
                trends within your venue.
              </SectionBody>
              <DetailsWrapper>
                <List>
                  <li>
                    <span>Analytics Dashboard</span>
                  </li>
                  <li>
                    <span>Missed Searches</span>
                  </li>
                  <li>
                    <span>Visitor Intent</span>
                  </li>
                  <li>
                    <span>Raw Data Exports</span>
                  </li>
                </List>
              </DetailsWrapper>
              <StyledLink href="/mapping/analytics/">
                Explore Analytics
              </StyledLink>
            </InfoWrapper>
          </VisibilityProvider>
        </Grid>
      </CMSFunctionsWrapper>
    </>
  )
}

export default CMSFunctions
